import React, {useEffect, useRef, useState} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Button,
	FlexColumn,
	FlexRow,
	Label,
	Modal,
	Pill,
	PillVariant,
	Text,
	TextArea,
	TextField,
} from '@forecast-it/design-system';
import {AgentFormValues, DEFAULT_AGENT_FORM, validateAgentForm} from '../shared/novaAgentForm';

export interface NovaCategoryOption {
	categoryId: string;
	name: string;
}

const SUBMIT_BUTTON_TEST_ID = 'nova-create-agent-submit';

export interface CreateAgentModalProps {
	categories: NovaCategoryOption[];
	existingNames: string[];
	onSubmit: (values: AgentFormValues) => Promise<void>;
	onCreateCategory: (name: string) => Promise<NovaCategoryOption>;
	onClose: () => void;
}

// Pill renders no click handler and accepts no data-cy (it's a purely decorative status/tag
// element), and the design system's selection controls don't fit either: ComboBox keeps its
// options hidden until the field is opened (the category chips must be visible immediately), and
// SegmentedControl/ToggleGroup are built for a small fixed set of tabs, not a dynamic,
// user-extensible list of per-item, individually-addressable chips. This unstyled button is the
// minimal wrapper needed to make a Pill clickable and individually selectable; it adds no visual
// styling of its own beyond resetting default button chrome.
const ChipButton = styled.button`
	all: unset;
	display: inline-flex;
	cursor: pointer;
`;

// The submit button is intentionally never natively `disabled` (see handleSubmit below), so DS
// Button's `:disabled` visual treatment never applies. This restores a minimal equivalent look for
// its `aria-disabled` state so an incomplete form doesn't appear fully active.
const SubmitButtonAriaDisabledStyle = createGlobalStyle`
	[data-cy="${SUBMIT_BUTTON_TEST_ID}"][aria-disabled='true'] {
		opacity: 0.5;
		cursor: default;
	}
`;

export function CreateAgentModal({
	categories,
	existingNames,
	onSubmit,
	onCreateCategory,
	onClose,
}: CreateAgentModalProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [values, setValues] = useState<AgentFormValues>(DEFAULT_AGENT_FORM);
	const [createdCategories, setCreatedCategories] = useState<NovaCategoryOption[]>([]);
	const [isAddingCategory, setIsAddingCategory] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [isCreatingCategory, setIsCreatingCategory] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [createCategoryError, setCreateCategoryError] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitFailed, setSubmitFailed] = useState(false);
	// On a successful submit the parent closes (unmounts) this modal synchronously, so guard the
	// post-await state writes to avoid a React "setState on an unmounted component" warning.
	const isMountedRef = useRef(true);
	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[]
	);

	const allCategories = [
		...categories,
		...createdCategories.filter(created => !categories.some(category => category.categoryId === created.categoryId)),
	];

	const {isValid, missing, duplicateName} = validateAgentForm(values, existingNames);

	const isFieldInvalid = (field: keyof AgentFormValues): boolean => submitAttempted && missing.includes(field);
	const requiredFieldMessage = formatMessage({
		id: 'nova.createAgent.fieldRequiredError',
		defaultMessage: 'This field is required.',
	});

	const updateField = <K extends keyof AgentFormValues>(field: K, fieldValue: AgentFormValues[K]): void => {
		setValues(previous => ({...previous, [field]: fieldValue}));
	};

	const handleCreateCategory = async (): Promise<void> => {
		const trimmedName = newCategoryName.trim();
		if (!trimmedName || isCreatingCategory) {
			return;
		}
		setIsCreatingCategory(true);
		setCreateCategoryError(false);
		try {
			const created = await onCreateCategory(trimmedName);
			setCreatedCategories(previous => [...previous, created]);
			updateField('categoryId', created.categoryId);
			setNewCategoryName('');
			setIsAddingCategory(false);
		} catch {
			setCreateCategoryError(true);
		} finally {
			setIsCreatingCategory(false);
		}
	};

	// TextField doesn't expose an onKeyDown prop, so the Enter-to-confirm shortcut is wired on
	// this plain wrapping div instead: the input's native keydown event still bubbles up to it.
	const handleNewCategoryKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === 'Enter') {
			event.preventDefault();
			void handleCreateCategory();
		}
	};

	// The submit button is never natively `disabled` (a native `disabled`
	// button fires no click event in real browsers, so a click on it would
	// never reach this handler and "submit attempted" could never be
	// flipped). Instead the button stays clickable and is only marked
	// `aria-disabled` while the form is incomplete or a submit is already
	// in flight; clicking it in that state reveals the missing-field
	// indicators (or is a no-op while in flight) without double-submitting.
	const handleSubmit = async (): Promise<void> => {
		if (!isValid || submitting) {
			setSubmitAttempted(true);
			return;
		}
		setSubmitting(true);
		setSubmitFailed(false);
		try {
			await onSubmit(values);
		} catch {
			if (isMountedRef.current) {
				setSubmitFailed(true);
			}
		} finally {
			if (isMountedRef.current) {
				setSubmitting(false);
			}
		}
	};

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={onClose} closeOnInteractOutside={false}>
			<SubmitButtonAriaDisabledStyle />
			<Modal.Title>{formatMessage({id: 'nova.createAgent.title.create', defaultMessage: 'New agent'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<FlexColumn gap={'xs'}>
						<TextField
							label={formatMessage({id: 'nova.createAgent.nameLabel', defaultMessage: 'Agent name'})}
							data-cy="nova-agent-name"
							value={values.name}
							onValueChange={value => updateField('name', value)}
							placeholder={formatMessage({
								id: 'nova.createAgent.namePlaceholder',
								defaultMessage: 'e.g. landing-page-writer',
							})}
							size={TextField.SIZE.LARGE}
							width={TextField.WIDTH.FLEX}
							hasError={isFieldInvalid('name')}
							errorText={isFieldInvalid('name') ? requiredFieldMessage : undefined}
						/>
						{duplicateName && (
							<Text color="error" data-cy="nova-duplicate-name-warning">
								{formatMessage({
									id: 'nova.createAgent.duplicateNameWarning',
									defaultMessage: 'An agent with this name already exists.',
								})}
							</Text>
						)}
					</FlexColumn>

					<FlexColumn gap={'xs'}>
						{/* The chip group is hand-rolled, so it has no design-system input label of its
						    own — the required marker is the design-system Label's `*` rather than
						    hand-rolled hint typography beside it. */}
						<Label required data-cy="nova-agent-category-required">
							{formatMessage({id: 'nova.createAgent.categoryLabel', defaultMessage: 'Category'})}
						</Label>
						<div role="group">
							<FlexRow wrap gap={'s'} data-cy="nova-agent-category">
								{allCategories.map(category => (
									<ChipButton
										key={category.categoryId}
										type="button"
										data-cy={`nova-category-${category.categoryId}`}
										onClick={() => updateField('categoryId', category.categoryId)}
									>
										<Pill
											variant={
												values.categoryId === category.categoryId
													? PillVariant.HIGHLIGHT
													: PillVariant.NEUTRAL
											}
										>
											{category.name}
										</Pill>
									</ChipButton>
								))}
								{!isAddingCategory && (
									<Button
										emphasis="low"
										icon="plus"
										data-cy="nova-add-category"
										onPress={() => setIsAddingCategory(true)}
									>
										{formatMessage({
											id: 'nova.createAgent.newCategoryPrompt',
											defaultMessage: 'Type a new category',
										})}
									</Button>
								)}
							</FlexRow>
						</div>
						{isFieldInvalid('categoryId') && (
							<Text color="error" data-cy="nova-agent-category-error">
								{requiredFieldMessage}
							</Text>
						)}
						{isAddingCategory && (
							<div onKeyDown={handleNewCategoryKeyDown}>
								<FlexRow gap={'s'} alignItems={'center'}>
									<TextField
										data-cy="nova-new-category-name"
										value={newCategoryName}
										onValueChange={setNewCategoryName}
										placeholder={formatMessage({
											id: 'nova.createAgent.newCategoryPlaceholder',
											defaultMessage: 'New category name',
										})}
										size={TextField.SIZE.LARGE}
										width={TextField.WIDTH.FLEX}
									/>
									<Button
										emphasis="low"
										data-cy="nova-confirm-new-category"
										disabled={isCreatingCategory || !newCategoryName.trim()}
										onPress={() => void handleCreateCategory()}
									>
										{formatMessage({id: 'nova.createAgent.addCategory', defaultMessage: 'Add'})}
									</Button>
								</FlexRow>
								{createCategoryError && (
									<Text color="error" data-cy="nova-create-category-error">
										{formatMessage({
											id: 'nova.createAgent.createCategoryError',
											defaultMessage: "Couldn't create category. Please try again.",
										})}
									</Text>
								)}
							</div>
						)}
					</FlexColumn>

					<TextField
						label={formatMessage({id: 'nova.createAgent.titleLabel', defaultMessage: 'Title'})}
						data-cy="nova-agent-title"
						value={values.title}
						onValueChange={value => updateField('title', value)}
						placeholder={formatMessage({
							id: 'nova.createAgent.titlePlaceholder',
							defaultMessage: 'Shown to users — e.g. Landing page writer',
						})}
						size={TextField.SIZE.LARGE}
						width={TextField.WIDTH.FLEX}
						hasError={isFieldInvalid('title')}
						errorText={isFieldInvalid('title') ? requiredFieldMessage : undefined}
					/>

					<TextArea
						label={formatMessage({id: 'nova.createAgent.descriptionLabel', defaultMessage: 'Description'})}
						data-cy="nova-agent-description"
						value={values.description}
						onChange={(value: string) => updateField('description', value)}
						onBlur={() => undefined}
						isOptional={false}
						placeholder={formatMessage({
							id: 'nova.createAgent.descriptionPlaceholder',
							defaultMessage: 'A short summary of what this agent does',
						})}
						width={TextArea.WIDTH.FLEX}
						hasError={isFieldInvalid('description')}
						errorText={isFieldInvalid('description') ? requiredFieldMessage : undefined}
					/>

					<TextArea
						label={formatMessage({id: 'nova.createAgent.userPromptLabel', defaultMessage: 'User prompt'})}
						data-cy="nova-agent-user-prompt"
						value={values.userPromptTemplate}
						onChange={(value: string) => updateField('userPromptTemplate', value)}
						onBlur={() => undefined}
						isOptional={true}
						placeholder={formatMessage({
							id: 'nova.createAgent.userPromptPlaceholder',
							// The token's braces are ICU-quoted (wrapped in single quotes) so react-intl
							// renders `{{task_title}}` literally instead of reading it as a message
							// argument. The quotes themselves are the escape and are not rendered.
							defaultMessage: "Use variables from the library, e.g. Draft a landing page for '{{task_title}}'…",
						})}
						height={110}
						width={TextArea.WIDTH.FLEX}
					/>

					<TextArea
						label={formatMessage({id: 'nova.createAgent.systemPromptLabel', defaultMessage: 'System prompt'})}
						data-cy="nova-agent-system-prompt"
						value={values.systemPrompt}
						onChange={(value: string) => updateField('systemPrompt', value)}
						onBlur={() => undefined}
						isOptional={true}
						placeholder={formatMessage({
							id: 'nova.createAgent.systemPromptPlaceholder',
							defaultMessage: 'Standing behaviour & rules for this agent…',
						})}
						height={110}
						width={TextArea.WIDTH.FLEX}
					/>

					{submitFailed && (
						<Text color="error" data-cy="nova-create-agent-submit-failed">
							{formatMessage({
								id: 'nova.createAgent.submitFailed',
								defaultMessage: 'Something went wrong saving this agent. Please try again.',
							})}
						</Text>
					)}

					<Text color="auxiliary" data-cy="nova-create-agent-footer-note">
						{formatMessage({
							id: 'nova.createAgent.footerNote',
							defaultMessage: 'Enable it in Nova Studio for users to choose it.',
						})}
					</Text>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={() => void handleSubmit()}
					aria-disabled={!isValid || submitting}
					dataCy={SUBMIT_BUTTON_TEST_ID}
				>
					{formatMessage({id: 'nova.createAgent.submitCreate', defaultMessage: 'Create agent'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} dataCy="nova-create-agent-cancel">
					{formatMessage({id: 'nova.createAgent.cancel', defaultMessage: 'Cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
